import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

const DemoPageContainer = styled.div`
  min-height: 100vh;
  padding: 6rem 2rem 4rem;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 3rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const AgentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 2px solid ${props => props.$primaryColor}20;
`;

const AgentAvatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${props => props.$primaryColor} 0%, ${props => props.$accentColor} 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 2rem;
  flex-shrink: 0;
`;

const AgentInfo = styled.div`
  flex: 1;
`;

const AgentName = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 0.5rem;
  color: ${props => props.$primaryColor};

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const AgentTagline = styled.p`
  font-size: 1.125rem;
  color: #666;
  margin: 0;
`;

const Section = styled.section`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: ${props => props.$primaryColor};
`;

const SectionContent = styled.p`
  font-size: 1rem;
  color: #555;
  line-height: 1.8;
`;

const SuggestedPrompts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const PromptButton = styled.button`
  padding: 1rem 1.5rem;
  background: ${props => props.$primaryColor}10;
  border: 2px solid ${props => props.$primaryColor}30;
  border-radius: 12px;
  color: ${props => props.$primaryColor};
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;

  &:hover {
    background: ${props => props.$primaryColor}20;
    border-color: ${props => props.$primaryColor};
    transform: translateX(4px);
  }
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-column: 1;
  }
`;

const InfoCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
`;

const InfoTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 1rem;
  color: ${props => props.$primaryColor};
`;

const InfoText = styled.p`
  font-size: 0.9375rem;
  color: #666;
  line-height: 1.6;
  margin: 0.5rem 0;
`;

const CodeBlock = styled.pre`
  background: #f5f5f5;
  padding: 1rem;
  border-radius: 8px;
  overflow-x: auto;
  font-size: 0.875rem;
  margin: 1rem 0;
`;

const agentsData = {
  'executive-coach': {
    tenantId: 'demo-executive-coach',
    name: "Sarah Chen Coaching",
    tagline: "Executive Leadership & Strategic Growth",
    description: "Sarah's AI assistant specializes in executive leadership, strategic thinking, and team dynamics. Drawing from evidence-based leadership frameworks, it helps leaders clarify their vision, make better decisions, and build high-performing teams.",
    focus: "Executive leadership, delegation, strategic planning, team dynamics, career transitions to C-suite",
    prompts: [
      "I'm struggling with delegation. My team seems overwhelmed but I don't know how to help.",
      "What's the biggest leadership challenge you're navigating right now?",
      "I need to make a tough decision about restructuring my team.",
      "How can I improve communication with my executive team?"
    ],
    primaryColor: "#1B365D",
    accentColor: "#C5A572",
    initial: "S"
  },
  'wellness-coach': {
    tenantId: 'demo-wellness-coach',
    name: "Maya Rodriguez Wellness",
    tagline: "Mindfulness, Balance & Sustainable Self-Care",
    description: "Maya's AI companion draws from mindfulness practices, positive psychology, and holistic wellness principles. It helps people build sustainable self-care routines, manage stress, and cultivate inner balance through small, consistent actions.",
    focus: "Mindfulness, stress management, sleep habits, self-care routines, work-life balance, emotional resilience",
    prompts: [
      "I'm feeling really overwhelmed lately and don't know where to start.",
      "How can I build better morning routines?",
      "I struggle with work-life balance. Any suggestions?",
      "What are some mindfulness practices I can start today?"
    ],
    primaryColor: "#2D5F4A",
    accentColor: "#E8C170",
    initial: "M"
  },
  'bpo-support': {
    tenantId: 'demo-bpo-support',
    name: "Pacific Customer Solutions",
    tagline: "Tier 1 Customer Support (English & Taglish)",
    description: "Pacific's AI agent handles Tier 1 customer inquiries for telecom and financial services clients. It responds in English or Taglish depending on the customer's preference, resolves common issues quickly, and escalates complex cases with full context.",
    focus: "Order status, billing inquiries, account management, password resets, service activation, complaint routing",
    prompts: [
      "Magkano po yung monthly bill ko? (How much is my monthly bill?)",
      "I need to check my account balance.",
      "Paano po mag-activate ng new service? (How to activate a new service?)",
      "I have a billing issue I need help with."
    ],
    primaryColor: "#0F4C81",
    accentColor: "#FF6B35",
    initial: "P"
  },
  'realestate': {
    tenantId: 'demo-realestate',
    name: "Metro Property Group",
    tagline: "Metro Manila, Cavite & Cebu Properties",
    description: "Metro Property Group's AI assistant helps buyers find the right property across their portfolio of condominiums and house-and-lot projects. It provides unit availability, pricing, payment schemes, and schedules site visits. Communicates in English or Taglish.",
    focus: "Property inquiry, unit availability, pricing computation, site visit scheduling, buyer qualification",
    prompts: [
      "May available pa bang 2BR sa Makati? (Any 2BR available in Makati?)",
      "What's the monthly payment for a house and lot in Cavite?",
      "I'm looking for a condo near the airport, what do you have?",
      "Ano po yung payment scheme ninyo? (What's your payment scheme?)"
    ],
    primaryColor: "#8B1A1A",
    accentColor: "#D4AF37",
    initial: "M"
  }
};

function DemoPage() {
  const { agentSlug } = useParams();
  const agent = agentsData[agentSlug];
  const [scriptLoaded, setScriptLoaded] = useState(false);

  useEffect(() => {
    // Load widget script
    const script = document.createElement('script');
    script.src = 'https://agents-valyss.web.app/widget.js';
    script.setAttribute('data-tenant-id', agent.tenantId);
    script.async = true;
    script.onload = () => setScriptLoaded(true);
    document.body.appendChild(script);

    return () => {
      // Cleanup
      document.body.removeChild(script);
      const container = document.getElementById('valyss-widget-container');
      if (container) {
        container.remove();
      }
    };
  }, [agent.tenantId]);

  if (!agent) {
    return (
      <DemoPageContainer>
        <div style={{ textAlign: 'center', padding: '4rem' }}>
          <h1>Agent not found</h1>
          <p>The requested demo agent doesn't exist.</p>
        </div>
      </DemoPageContainer>
    );
  }

  const openChat = () => {
    if (window.ValyssWidget) {
      window.ValyssWidget.open();
    }
  };

  return (
    <DemoPageContainer>
      <ContentWrapper>
        <MainContent>
          <AgentHeader $primaryColor={agent.primaryColor}>
            <AgentAvatar 
              $primaryColor={agent.primaryColor}
              $accentColor={agent.accentColor}
            >
              {agent.initial}
            </AgentAvatar>
            <AgentInfo>
              <AgentName $primaryColor={agent.primaryColor}>
                {agent.name}
              </AgentName>
              <AgentTagline>{agent.tagline}</AgentTagline>
            </AgentInfo>
          </AgentHeader>

          <Section>
            <SectionTitle $primaryColor={agent.primaryColor}>
              About This Agent
            </SectionTitle>
            <SectionContent>{agent.description}</SectionContent>
          </Section>

          <Section>
            <SectionTitle $primaryColor={agent.primaryColor}>
              Focus Areas
            </SectionTitle>
            <SectionContent>{agent.focus}</SectionContent>
          </Section>

          <Section>
            <SectionTitle $primaryColor={agent.primaryColor}>
              Try These Prompts
            </SectionTitle>
            <SuggestedPrompts>
              {agent.prompts.map((prompt, index) => (
                <PromptButton
                  key={index}
                  $primaryColor={agent.primaryColor}
                  onClick={openChat}
                >
                  {prompt}
                </PromptButton>
              ))}
            </SuggestedPrompts>
          </Section>
        </MainContent>

        <Sidebar>
          <InfoCard>
            <InfoTitle $primaryColor={agent.primaryColor}>
              How It Works
            </InfoTitle>
            <InfoText>
              Click the chat bubble in the bottom-right corner to start a conversation with this AI agent.
            </InfoText>
            <InfoText>
              The agent will remember your conversation across page refreshes.
            </InfoText>
            <InfoText>
              {scriptLoaded ? '✓ Widget loaded' : '⏳ Loading widget...'}
            </InfoText>
          </InfoCard>

          <InfoCard>
            <InfoTitle $primaryColor={agent.primaryColor}>
              Embed on Your Site
            </InfoTitle>
            <InfoText>
              Add this single line to your website:
            </InfoText>
            <CodeBlock>{`<script src="https://agents-valyss.web.app/widget.js" data-tenant-id="${agent.tenantId}"></script>`}</CodeBlock>
            <InfoText style={{ fontSize: '0.875rem', color: '#999', marginTop: '1rem' }}>
              That's it! The chat widget will appear on your site with full branding.
            </InfoText>
          </InfoCard>
        </Sidebar>
      </ContentWrapper>
    </DemoPageContainer>
  );
}

export default DemoPage;
